import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { queryKeys } from '@/lib/query-keys';
import type {
  AgreementTemplate,
  AgreementTemplateInsert,
  AgreementTemplateUpdate,
  ClientAgreement,
  ClientAgreementInsert,
  ClientAgreementUpdate,
  ClientAgreementWithDetails,
  AgreementOrgSettings,
  AgreementOrgSettingsUpdate,
  AgreementType,
  AgreementStatus,
  AgreementDeliverable,
  AgreementDeliverableInsert,
  AgreementPaymentTerm,
  AgreementPaymentTermInsert,
  AgreementSLA,
  AgreementSLAInsert,
  EsignatureAuditLog,
} from '@/types/agreements';
import {
  buildAgreementSummary,
  fetchAgreementStructuredRows,
  replaceAgreementStructuredRows,
} from './useAgreements.helpers';
import {
  formatAgreementMutationError,
  invalidateAgreementListQueries,
  invalidateAgreementTemplateQueries,
} from './useAgreements.mutation-helpers';

// =============================================================================
// Organization Settings Hook
// =============================================================================

export function useAgreementOrgSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: queryKeys.agreements.orgSettings,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agreement_org_settings')
        .select('*')
        .maybeSingle();

      // Handle expected cases: no settings or RLS blocking access
      if (error) {
        // Silently return null for expected errors (no settings, access denied)
        return null;
      }
      return data as AgreementOrgSettings | null;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: AgreementOrgSettingsUpdate) => {
      // Get current user's organization
      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .single();

      if (!membership) throw new Error('No organization found');

      if (settings) {
        // Update existing
        const { error } = await supabase
          .from('agreement_org_settings')
          .update(updates)
          .eq('id', settings.id);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase.from('agreement_org_settings').insert({
          organization_id: membership.organization_id,
          ...updates,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agreements.orgSettings });
      toast.success('Settings saved');
    },
    onError: (error) => {
      toast.error('Failed to save settings: ' + formatAgreementMutationError(error));
    },
  });

  return { settings, isLoading, updateSettings };
}

// =============================================================================
// Agreement Templates Hook
// =============================================================================

export function useAgreementTemplates(type?: AgreementType) {
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: type ? queryKeys.agreements.templatesByType(type) : queryKeys.agreements.templates,
    queryFn: async () => {
      let query = supabase
        .from('agreement_templates')
        .select('*')
        .eq('is_active', true)
        .order('name')
        .order('version', { ascending: false });

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as AgreementTemplate[];
    },
  });

  const createTemplate = useMutation({
    mutationFn: async (template: AgreementTemplateInsert) => {
      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .single();

      if (!membership) throw new Error('No organization found');

      const { data: user } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('agreement_templates')
        .insert({
          ...template,
          organization_id: membership.organization_id,
          created_by: user.user?.id,
          updated_by: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as AgreementTemplate;
    },
    onSuccess: () => {
      invalidateAgreementTemplateQueries(queryClient);
      toast.success('Template created');
    },
    onError: (error) => {
      toast.error('Failed to create template: ' + formatAgreementMutationError(error));
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: AgreementTemplateUpdate }) => {
      const { data: user } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('agreement_templates')
        .update({
          ...updates,
          updated_by: user.user?.id,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAgreementTemplateQueries(queryClient);
      toast.success('Template updated');
    },
    onError: (error) => {
      toast.error('Failed to update template: ' + formatAgreementMutationError(error));
    },
  });

  const createNewVersion = useMutation({
    mutationFn: async ({
      templateId,
      versionNotes,
    }: {
      templateId: string;
      versionNotes?: string;
    }) => {
      // Get current template
      const { data: current, error: fetchError } = await supabase
        .from('agreement_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError) throw fetchError;

      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .single();

      if (!membership) throw new Error('No organization found');

      const { data: user } = await supabase.auth.getUser();

      // Deactivate current version
      await supabase.from('agreement_templates').update({ is_active: false }).eq('id', templateId);

      // Create new version
      const { data, error } = await supabase
        .from('agreement_templates')
        .insert({
          organization_id: membership.organization_id,
          name: current.name,
          description: current.description,
          type: current.type,
          version: current.version + 1,
          version_notes: versionNotes,
          is_active: true,
          parent_template_id: templateId,
          content_json: current.content_json,
          has_deliverables: current.has_deliverables,
          has_payment_terms: current.has_payment_terms,
          has_slas: current.has_slas,
          created_by: user.user?.id,
          updated_by: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as AgreementTemplate;
    },
    onSuccess: () => {
      invalidateAgreementTemplateQueries(queryClient);
      toast.success('New version created');
    },
    onError: (error) => {
      toast.error('Failed to create version: ' + formatAgreementMutationError(error));
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('agreement_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAgreementTemplateQueries(queryClient);
      toast.success('Template deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete template: ' + formatAgreementMutationError(error));
    },
  });

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    createNewVersion,
    deleteTemplate,
  };
}

// =============================================================================
// Single Agreement Template Hook
// =============================================================================

export function useAgreementTemplate(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.agreements.template(id),
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('agreement_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as AgreementTemplate;
    },
    enabled: !!id,
  });
}

// =============================================================================
// Client Agreements Hook
// =============================================================================

export function useClientAgreements(filters?: {
  clientId?: string;
  status?: AgreementStatus;
  type?: AgreementType;
}) {
  const queryClient = useQueryClient();

  const { data: agreements = [], isLoading } = useQuery({
    queryKey: queryKeys.agreements.list(filters),
    queryFn: async () => {
      let query = supabase
        .from('client_agreements')
        .select(
          `
          *,
          clients:client_id (id, name),
          agreement_templates:template_id (id, name, type)
        `,
        )
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data.map((a) => ({
        ...a,
        client: a.clients,
        template: a.agreement_templates,
      })) as ClientAgreementWithDetails[];
    },
  });

  const createAgreement = useMutation({
    mutationFn: async (agreement: ClientAgreementInsert) => {
      const { data: membership } = await supabase
        .from('organization_members')
        .select('organization_id')
        .single();

      if (!membership) throw new Error('No organization found');

      const { data: user } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('client_agreements')
        .insert({
          ...agreement,
          organization_id: membership.organization_id,
          created_by: user.user?.id,
          updated_by: user.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ClientAgreement;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement created');
    },
    onError: (error) => {
      toast.error('Failed to create agreement: ' + formatAgreementMutationError(error));
    },
  });

  const updateAgreement = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ClientAgreementUpdate }) => {
      const { data: user } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('client_agreements')
        .update({
          ...updates,
          updated_by: user.user?.id,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement updated');
    },
    onError: (error) => {
      toast.error('Failed to update agreement: ' + formatAgreementMutationError(error));
    },
  });

  const deleteAgreement = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('client_agreements').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete agreement: ' + formatAgreementMutationError(error));
    },
  });

  const summary = buildAgreementSummary(agreements);

  return {
    agreements,
    isLoading,
    summary,
    createAgreement,
    updateAgreement,
    deleteAgreement,
  };
}

// =============================================================================
// Single Client Agreement Hook
// =============================================================================

export function useClientAgreement(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.agreements.detail(id),
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('client_agreements')
        .select(
          `
          *,
          clients:client_id (id, name),
          agreement_templates:template_id (id, name, type)
        `,
        )
        .eq('id', id)
        .single();

      if (error) throw error;

      // Get structured data
      const [deliverables, paymentTerms, slas] = await Promise.all([
        fetchAgreementStructuredRows('agreement_deliverables', id),
        fetchAgreementStructuredRows('agreement_payment_terms', id),
        fetchAgreementStructuredRows('agreement_slas', id),
      ]);

      return {
        ...data,
        client: data.clients,
        template: data.agreement_templates,
        deliverables: deliverables,
        payment_terms: paymentTerms,
        slas,
      } as ClientAgreementWithDetails;
    },
    enabled: !!id,
  });
}

// =============================================================================
// Agreement Audit Log Hook
// =============================================================================

export function useAgreementAuditLog(agreementId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.agreements.auditLog(agreementId),
    queryFn: async () => {
      if (!agreementId) return [];

      const { data, error } = await supabase
        .from('esignature_audit_log')
        .select('*')
        .eq('agreement_id', agreementId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EsignatureAuditLog[];
    },
    enabled: !!agreementId,
  });
}

// =============================================================================
// Agreement Actions Hook
// =============================================================================

export function useAgreementActions() {
  const queryClient = useQueryClient();

  const sendAgreement = useMutation({
    mutationFn: async ({
      agreementId,
      recipientEmail,
      recipientName,
      personalMessage,
    }: {
      agreementId: string;
      recipientEmail: string;
      recipientName: string;
      personalMessage?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('agreement-actions', {
        body: {
          action: 'send',
          agreement_id: agreementId,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          personal_message: personalMessage,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement sent to client');
    },
    onError: (error) => {
      toast.error('Failed to send agreement: ' + formatAgreementMutationError(error));
    },
  });

  const voidAgreement = useMutation({
    mutationFn: async ({ agreementId, reason }: { agreementId: string; reason?: string }) => {
      const { data, error } = await supabase.functions.invoke('agreement-actions', {
        body: {
          action: 'void',
          agreement_id: agreementId,
          reason,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement voided');
    },
    onError: (error) => {
      toast.error('Failed to void agreement: ' + formatAgreementMutationError(error));
    },
  });

  const sendReminder = useMutation({
    mutationFn: async (agreementId: string) => {
      const { data, error } = await supabase.functions.invoke('agreement-actions', {
        body: {
          action: 'remind',
          agreement_id: agreementId,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Reminder sent');
    },
    onError: (error) => {
      toast.error('Failed to send reminder: ' + formatAgreementMutationError(error));
    },
  });

  const countersign = useMutation({
    mutationFn: async ({
      agreementId,
      signerName,
      signerTitle,
    }: {
      agreementId: string;
      signerName: string;
      signerTitle?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('agreement-actions', {
        body: {
          action: 'countersign',
          agreement_id: agreementId,
          signer_name: signerName,
          signer_title: signerTitle,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateAgreementListQueries(queryClient);
      toast.success('Agreement countersigned');
    },
    onError: (error) => {
      toast.error('Failed to countersign: ' + formatAgreementMutationError(error));
    },
  });

  return {
    sendAgreement,
    voidAgreement,
    sendReminder,
    countersign,
  };
}

// =============================================================================
// Structured Data Hooks
// =============================================================================

export function useAgreementDeliverables(agreementId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: deliverables = [], isLoading } = useQuery({
    queryKey: queryKeys.agreements.deliverables(agreementId),
    queryFn: async () => {
      if (!agreementId) return [];
      return fetchAgreementStructuredRows('agreement_deliverables', agreementId);
    },
    enabled: !!agreementId,
  });

  const upsertDeliverables = useMutation({
    mutationFn: async (items: AgreementDeliverableInsert[]) => {
      if (!agreementId) throw new Error('No agreement ID');

      await replaceAgreementStructuredRows('agreement_deliverables', agreementId, items);
    },
    onSuccess: () => {
      if (agreementId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.agreements.deliverables(agreementId),
        });
      }
    },
  });

  return { deliverables, isLoading, upsertDeliverables };
}

export function useAgreementPaymentTerms(agreementId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: paymentTerms = [], isLoading } = useQuery({
    queryKey: queryKeys.agreements.paymentTerms(agreementId),
    queryFn: async () => {
      if (!agreementId) return [];
      return fetchAgreementStructuredRows('agreement_payment_terms', agreementId);
    },
    enabled: !!agreementId,
  });

  const upsertPaymentTerms = useMutation({
    mutationFn: async (items: AgreementPaymentTermInsert[]) => {
      if (!agreementId) throw new Error('No agreement ID');

      await replaceAgreementStructuredRows('agreement_payment_terms', agreementId, items);
    },
    onSuccess: () => {
      if (agreementId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.agreements.paymentTerms(agreementId),
        });
      }
    },
  });

  return { paymentTerms, isLoading, upsertPaymentTerms };
}

export function useAgreementSLAs(agreementId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: slas = [], isLoading } = useQuery({
    queryKey: queryKeys.agreements.slas(agreementId),
    queryFn: async () => {
      if (!agreementId) return [];
      return fetchAgreementStructuredRows('agreement_slas', agreementId);
    },
    enabled: !!agreementId,
  });

  const upsertSLAs = useMutation({
    mutationFn: async (items: AgreementSLAInsert[]) => {
      if (!agreementId) throw new Error('No agreement ID');

      await replaceAgreementStructuredRows('agreement_slas', agreementId, items);
    },
    onSuccess: () => {
      if (agreementId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.agreements.slas(agreementId) });
      }
    },
  });

  return { slas, isLoading, upsertSLAs };
}
